import Loading from "@/components/Loading";
import { AuthSignUpPageContainer } from "@/components/pages/Auth/AuthSignUpPageOutlet";
import { Retailer } from "@/redux/types";
import { getEntityImage, UNIVERSAL_STORE_SITE } from "@brandclub/common-ui";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, IconButton, InputAdornment, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { CheckoutInputV2 } from "../../../../../CheckoutInputV2";
import AnimatedSubmittingButton from "../../common/AnimatedSubmittingButton";
import { useAppSelector } from "@/redux/hooks";

import { StyledLink } from "@/components/AppNavigation/StyledLink";
const Container = styled(AuthSignUpPageContainer)(({ theme }) => ({
  color: theme.palette.primary.main,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  ".retailer-connect-login-container": {
    display: "flex",
    flexDirection: "column",
  },
  ".connect-title": {
    fontWeight: 700,
    fontSize: 22,
    marginBottom: "12px",
    [theme.breakpoints.down("xs")]: {
      fontSize: 18,
      marginBottom: "5px",
    },
    textAlign: "center",
  },
  ".connect-subtitle": {
    fontSize: 16,
    textAlign: "center",
    fontWeight: 500,
    marginBottom: "24px",
    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
      marginBottom: "10px",
    },
  },
  ".reset-password-button": {
    background: "transparent",
    border: "none",

    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    margin: "16px 0",
    color: theme.palette.primary.main,

    textDecoration: "underline",
    "&:disabled": {
      color: "text.disabled",
      cursor: "not-allowed",
    },
  },
}));

export const RetailerConnectLogin = ({
  onSaveCredentials,
  onChange,
  closeEdit,
  currentUsername,
  currentPassword,
  retailer,
}: {
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  onChange?: (event: {
    username: string;
    password: string;
    edited: boolean;
  }) => void;
  closeEdit: () => void;
  currentUsername: string;
  currentPassword: string;
  retailer?: Retailer;
}) => {
  const [username, setUsername] = useState<string>(currentUsername);
  const [password, setPassword] = useState<string>(currentPassword);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );
  const linkPrefix =
    domainConfig?.storeBrandingType === "CustomDTCStore"
      ? UNIVERSAL_STORE_SITE
      : "";

  const openResetPassword = async () => {
    if ((retailer as any)?.forgotPasswordUrl) {
      window.open((retailer as any)?.forgotPasswordUrl, "_blank");
      return;
    }
    switch (retailer?.retailerId) {
      case 1:
        window.open(
          "https://www.amazon.com/gp/css/account/forgot-password/email.html",
          "_blank"
        );
        break;
      case 2:
        window.open("https://www.walmart.com/account/forgotpassword", "_blank");
        break;
      case 3:
        window.open("https://www.bestbuy.com/identity/global/signin", "_blank");
        break;
      case 4:
        window.open(
          "https://www.target.com/account/settings/signinsecurity",
          "_blank"
        );
        break;
      default:
        break;
    }
  };

  const edited = username !== currentUsername || password !== currentPassword;

  useEffect(() => {
    onChange?.({ username, password, edited });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, username, edited]);

  const getUserNameFieldHeading = (retailer: Retailer) => {
    if (retailer.retailerLoginUsernameFieldTypes?.length) {
      const supportedFieldTypes = retailer.retailerLoginUsernameFieldTypes;
      if (
        supportedFieldTypes.includes("email") &&
        supportedFieldTypes.includes("phoneNumber")
      ) {
        return "Email or mobile phone";
      }
    }
    return "Email";
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container>
      {!retailer ? (
        <Box
          className="retailer-connect-login-container"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Loading dot />
        </Box>
      ) : (
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          maxWidth={425}
        >
          <Box
            className="retailer-connect-login-container"
            sx={{
              maxWidth: 380,
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "column",
              },
              alignItems: "center",
              justifyContent: "flex-start",
              bgcolor: "white",
              borderRadius: 4,
            }}
          >
            {/* Retailer Icon */}
            <Box
              sx={{
                bgcolor: "#e6f3f7",
                borderRadius: "50%",
                width: 72,
                height: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <img
                className="retailer_logo"
                alt="r_logo"
                style={{
                  width: "72px",
                  height: "72px",
                  objectFit: "contain",
                }}
                src={getEntityImage(retailer?.retailerId, "retailer")}
              />
            </Box>
            {/* Title */}
            <Box className="connect-title">
              Log in to {retailer?.displayName || "Retailer"}
            </Box>
            {/* Subtitle */}
            <Box className="connect-subtitle">
              Connect your {retailer?.displayName || "Retailer"} account to earn
              rewards and get expedited shipping.
            </Box>
            {/* Inputs */}
            <Box sx={{ width: "100%", mb: 2 }}>
              <CheckoutInputV2
                label={getUserNameFieldHeading(retailer || ({} as Retailer))}
                style={{ marginBottom: 20 }}
                onChange={(e) => {
                  setUsername(e.target.value as any);
                }}
                sx={{ maxWidth: "100%" }}
                disabled={submitting}
                value={username}
                InputProps={{
                  autoComplete: "username",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
              />
              <CheckoutInputV2
                label="Password"
                type={showPassword ? "text" : "password"}
                style={{ marginBottom: 0 }}
                onChange={(e) => {
                  setPassword(e.target.value as any);
                }}
                sx={{ maxWidth: "100%" }}
                disabled={submitting}
                value={password}
                InputProps={{
                  autoComplete: "current-password",
                  name: "password-checkout-" + Math.random(),
                  inputMode: "text",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        size="small"
                        disabled={submitting}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {/* Disclaimer */}

            {/* Buttons */}
            <AnimatedSubmittingButton
              onClick={async () => {
                setSubmitting(true);
                await onSaveCredentials(username, password ?? "");
                setSubmitting(false);
              }}
              loading={submitting}
              disabled={submitting || !username || !password || !edited}
              text="Submit"
              animationCallback={closeEdit}
            />
            <Box
              component="button"
              onClick={openResetPassword}
              disabled={submitting}
              className="reset-password-button"
            >
              Reset password
            </Box>
          </Box>
          <Box
            sx={() => ({
              fontSize: "12px",
              mb: 3,
              mt: 1,
              opacity: 0.5,
              textAlign: "center",
            })}
          >
            By providing your {retailer?.displayName || "Retailer"} credentials,
            you authorize Brandclub to place this order on your behalf in
            accordance with our{" "}
            <StyledLink to={linkPrefix + "/terms"} target="_blank">
              Terms
            </StyledLink>
            .
          </Box>
        </Box>
      )}
    </Container>
  );
};
